import authenticationServiceApi from '../api/authentication.service.api';
import { updateToken } from '../store/actions/auth.actions';
import { ServiceBase } from './service.base';

/**
 * @typedef {'success' | 'auth_failed' | 'network_error'} RefreshTokenResult
 * - success: token refreshed, Redux state updated
 * - auth_failed: refresh token is invalid/expired (401) — user must re-authenticate
 * - network_error: could not reach the server — safe to keep session for offline use
 */

const REFRESH_RESULT = Object.freeze({
  SUCCESS: 'success',
  AUTH_FAILED: 'auth_failed',
  NETWORK_ERROR: 'network_error',
});

const MAX_RETRIES = 2;
const RETRY_DELAYS = [1000, 2000];

class AuthService extends ServiceBase {
  /**
   * Refresh access token with retry logic.
   * @returns {Promise<RefreshTokenResult>}
   * @memberof AuthService
   */
  async refreshToken() {
    const { auth, organization } = this.state;
    const refreshToken = auth?.refreshToken || null;
    const userId = auth?.user?.id || auth?.id || auth?.refreshTokenDetails.id || null;
    const organizationId =
      organization?.id ||
      auth?.user?.organizationId ||
      auth?.organizationId ||
      auth?.refreshTokenDetails?.organizationId ||
      null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const res = await authenticationServiceApi.refreshToken({
          refreshToken,
          userId,
          organizationId,
        });

        const data = res?.data;

        if (res.error) {
          const status = res.error?.response?.status;
          const errorMessage = data?.message || res.error?.message || 'Unknown error';

          // 401 means the refresh token itself is invalid — no point retrying
          if (status === 401) {
            console.log(`Fail to refresh access token - token invalid: ${errorMessage}`);
            return REFRESH_RESULT.AUTH_FAILED;
          }

          // Server responded with an error — retry
          if (attempt < MAX_RETRIES) {
            console.log(
              `Fail to refresh access token (attempt ${attempt + 1}/${MAX_RETRIES + 1}) - ${errorMessage}`,
            );
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
            continue;
          }

          console.log(
            `Fail to refresh access token after ${MAX_RETRIES + 1} attempts - ${errorMessage}`,
          );
          return REFRESH_RESULT.NETWORK_ERROR;
        }

        console.log('Token refresh - Succeeded');
        this.dispatch(updateToken(data));
        return REFRESH_RESULT.SUCCESS;
      } catch (e) {
        if (attempt < MAX_RETRIES) {
          console.log(`Fail to refresh access token (attempt ${attempt + 1}) - ${e}`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
          continue;
        }

        console.log(
          `Fail to refresh access token - network unavailable after ${MAX_RETRIES + 1} attempts`,
        );
        return REFRESH_RESULT.NETWORK_ERROR;
      }
    }

    return REFRESH_RESULT.NETWORK_ERROR;
  }
}

export { REFRESH_RESULT };
export default new AuthService();
